<!-- src/App.vue -->
<template>
    <div id="app">
        <!-- 动态粒子背景 -->
        <div class="dynamic-background" ref="canvasContainer">
            <!-- 双背景层实现平滑过渡 -->
            <div class="gradient-background gradient-layer1" ref="gradientLayer1"></div>
            <div class="gradient-background gradient-layer2" ref="gradientLayer2"></div>
            <canvas ref="particleCanvas"></canvas>
        </div>

        <!-- 前景内容容器 -->
        <div class="foreground-container">
            <NotificationManager ref="notificationManager" />
            <NavBar v-if="showNavBar" />
            <div class="content">
                <router-view v-slot="{ Component }">
                    <transition name="page-fade" mode="out-in">
                        <component :is="Component" />
                    </transition>
                </router-view>
            </div>
            <footer class="app-footer">
                <p>&copy;&copy;&copy;&copy;&copy; 图书管理系统 {{ new Date().getFullYear() }} | 版本 1.0.0</p>
            </footer>
        </div>
    </div>
</template>

<script>
    import NavBar from '@/components/NavBar.vue'
    import { computed, onMounted, ref, onUnmounted } from 'vue'
    import { useRoute } from 'vue-router'
    import NotificationManager from './components/NotificationManager.vue';
    import { setNotificationManager, setupGlobalNotificationHandling } from '@/utils/notificationHandler';

    export default {
        name: 'App',
        components: {
            NavBar,
            NotificationManager
        },
        setup() {
            const route = useRoute()
            const showNavBar = computed(() => {
                return route.name !== 'Login' && route.name !== 'Register'
            })

            // 粒子背景相关代码
            const particleCanvas = ref(null);
            const canvasContainer = ref(null);
            const gradientLayer1 = ref(null);
            const gradientLayer2 = ref(null);
            let ctx = null;
            let particles = [];
            let animationFrameId = null;

            // 粒子类
            class Particle {
                constructor(x, y) {
                    this.x = x;
                    this.y = y;
                    this.size = Math.random() * 4 + 2;      // 半径
                    this.mass = this.size;                  // ✅ 质量 = 半径
                    this.speedX = (Math.random() * 2 - 1) * 1.5;
                    this.speedY = (Math.random() * 2 - 1) * 1.5;
                    this.color = `rgba(${Math.floor(Math.random() * 100 + 50)},
                         ${Math.floor(Math.random() * 100 + 50)},
                         ${Math.floor(Math.random() * 100 + 50)},`;

                    this.life = 0;
                    this.fadeInDuration = 300;
                }

                alpha() { return Math.min(this.life / this.fadeInDuration, 1); }

                // ✅ 计算速度大小（用于碰撞检测）
                speed() { return Math.hypot(this.speedX, this.speedY); }

                update() {
                    this.x += this.speedX;
                    this.y += this.speedY;

                    // 边界反弹（能量有损失，可忽略）
                    if (this.x - this.size < 0 || this.x + this.size > window.innerWidth) this.speedX *= -1;
                    if (this.y - this.size < 0 || this.y + this.size > window.innerHeight) this.speedY *= -1;

                    if (this.life < this.fadeInDuration) this.life += 16;
                }

                draw() {
                    const a = this.alpha();
                    ctx.fillStyle = `${this.color} ${a})`;
                    ctx.beginPath();
                    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            // 初始化粒子系统
            const initParticles = () => {
                const canvas = particleCanvas.value;
                const container = canvasContainer.value;
                if (!canvas || !container) return;

                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;

                /* ✅ 新增：GPU 加速开关 */
                canvas.style.willChange = 'transform';          // 关键 1
                ctx = canvas.getContext('2d', {
                    willReadFrequently: false                   // 关键 2
                });

                /* 以下原逻辑不变 */
                particles = [];
                const particleCount = Math.floor(window.innerWidth * window.innerHeight / 5000);
                for (let i = 0; i < particleCount; i++) {
                    particles.push(new Particle(
                        Math.random() * canvas.width,
                        Math.random() * canvas.height
                    ));
                }
                animateParticles();
            };

            // 动画循环
            const animateParticles = () => {
                ctx.clearRect(0, 0, particleCanvas.value.width, particleCanvas.value.height);

                // ✅ 先更新位置
                particles.forEach(p => p.update());

                // ✅ 再检测/处理碰撞
                resolveCollisions();

                // ✅ 最后绘制
                particles.forEach(p => p.draw());
                drawConnections();

                animationFrameId = requestAnimationFrame(animateParticles);
            };

            // 绘制粒子间的连线
            const drawConnections = () => {
                const maxDistance = 100;

                for (let i = 0; i < particles.length; i++) {
                    for (let j = i + 1; j < particles.length; j++) {
                        const dx = particles[i].x - particles[j].x;
                        const dy = particles[i].y - particles[j].y;
                        const distance = Math.sqrt(dx * dx + dy * dy);

                        if (distance < maxDistance) {
                            const opacityLine = (1 - distance / maxDistance) *
                                Math.min(particles[i].alpha(), particles[j].alpha());
                            ctx.strokeStyle = `rgba(0, 49, 96, ${opacityLine})`;
                            ctx.lineWidth = 0.5;
                            ctx.beginPath();
                            ctx.moveTo(particles[i].x, particles[i].y);
                            ctx.lineTo(particles[j].x, particles[j].y);
                            ctx.stroke();
                        }
                    }
                }
            };

            // ✅ 粒子碰撞检测（动量守恒）
            const resolveCollisions = () => {
                for (let i = 0; i < particles.length; i++) {
                    for (let j = i + 1; j < particles.length; j++) {
                        const p1 = particles[i];
                        const p2 = particles[j];

                        const dx = p2.x - p1.x;
                        const dy = p2.y - p1.y;
                        const dist = Math.hypot(dx, dy);

                        const minDist = p1.size + p2.size;

                        if (dist < minDist) {
                            // 防止粘连：先分离
                            const overlap = minDist - dist;
                            const separateX = (dx / dist) * overlap * 0.5;
                            const separateY = (dy / dist) * overlap * 0.5;
                            p1.x -= separateX;
                            p1.y -= separateY;
                            p2.x += separateX;
                            p2.y += separateY;

                            // 碰撞轴向
                            const nx = dx / dist;
                            const ny = dy / dist;

                            // 相对速度在碰撞轴向的投影
                            const relVelX = p2.speedX - p1.speedX;
                            const relVelY = p2.speedY - p1.speedY;
                            const relVelDot = relVelX * nx + relVelY * ny;

                            // 如果远离则忽略
                            if (relVelDot > 0) continue;

                            // 动量守恒（完全弹性碰撞）
                            const impulse = 2 * relVelDot / (p1.mass + p2.mass);

                            p1.speedX += impulse * p2.mass * nx;
                            p1.speedY += impulse * p2.mass * ny;
                            p2.speedX -= impulse * p1.mass * nx;
                            p2.speedY -= impulse * p1.mass * ny;
                        }
                    }
                }
            };

            let mouseMoveCounter = 0;
            const MOUSE_MOVE_THRESHOLD = 10; // 每移动10次生成一个粒子

            // 鼠标移动交互
            const handleMouseMove = (e) => {
                if (!particles.length) return;

                const mouseX = e.clientX;
                const mouseY = e.clientY;

                // 增加计数器
                mouseMoveCounter++;

                // 如果达到阈值，则生成粒子
                if (mouseMoveCounter >= MOUSE_MOVE_THRESHOLD) {
                    // 在鼠标周围创建互动效果
                    for (let i = 0; i < 1; i++) {
                        const x = mouseX;
                        const y = mouseY;

                        particles.push(new Particle(x, y));
                    }

                    // 重置计数器
                    mouseMoveCounter = 0;
                }

                // 限制粒子数量
                if (particles.length > 300) {
                    particles.splice(0, particles.length - 300);
                }
            };

            // 生成浅色 HSL 随机色
            const randomLightColor = () => {
                const hue = Math.floor(Math.random() * 360)
                const saturation = 60 + Math.floor(Math.random() * 20) // 60-80%
                const lightness = 80 + Math.floor(Math.random() * 10) // 80-90%
                return `hsl(${hue}, ${saturation}%, ${lightness}%)`
            }

            let timerId = null
            let isTransitioning = false
            let activeLayer = 1 // 当前激活的层 (1 或 2)

            // 更新渐变背景颜色（带过渡效果）
            const updateGradientColors = () => {
                if (isTransitioning) return
                isTransitioning = true

                // 确定当前层和下一层
                const currentLayer = activeLayer === 1 ? gradientLayer1.value : gradientLayer2.value
                const nextLayer = activeLayer === 1 ? gradientLayer2.value : gradientLayer1.value

                // 生成新颜色
                const colors = {
                    g1: randomLightColor(),
                    g2: randomLightColor(),
                    g3: randomLightColor(),
                    g4: randomLightColor(),
                    g5: randomLightColor()
                }

                // 设置下一层的颜色
                nextLayer.style.background = `linear-gradient(-45deg,
                    ${colors.g1} 0%,
                    ${colors.g2} 20%,
                    ${colors.g3} 40%,
                    ${colors.g4} 60%,
                    ${colors.g5} 80%,
                    ${colors.g1} 100%)`

                // 开始过渡效果
                nextLayer.classList.add('active')
                currentLayer.classList.add('fade-out')

                // 过渡完成后切换激活层
                setTimeout(() => {
                    currentLayer.classList.remove('active', 'fade-out')
                    nextLayer.classList.remove('fade-out')

                    // 切换激活层
                    activeLayer = activeLayer === 1 ? 2 : 1
                    isTransitioning = false
                }, 2000) // 与CSS过渡时间一致
            }

            // 窗口大小调整处理
            const handleResize = () => {
                if (particleCanvas.value) {
                    particleCanvas.value.width = window.innerWidth;
                    particleCanvas.value.height = window.innerHeight;
                }
            };

            onMounted(() => {
                initParticles();
                window.addEventListener('resize', handleResize);
                window.addEventListener('mousemove', handleMouseMove);

                // 初始化两层背景
                const colors = {
                    g1: randomLightColor(),
                    g2: randomLightColor(),
                    g3: randomLightColor(),
                    g4: randomLightColor(),
                    g5: randomLightColor()
                }

                gradientLayer1.value.style.background = `linear-gradient(-45deg,
                    ${colors.g1} 0%,
                    ${colors.g2} 20%,
                    ${colors.g3} 40%,
                    ${colors.g4} 60%,
                    ${colors.g5} 80%,
                    ${colors.g1} 100%)`
                gradientLayer1.value.classList.add('active')

                // 首次更新后设置定时器
                timerId = setInterval(updateGradientColors, 1000) // 每 60s 换一套
            });

            onUnmounted(() => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                }
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('mousemove', handleMouseMove);
                clearInterval(timerId)
            });

            return {
                showNavBar,
                particleCanvas,
                canvasContainer,
                gradientLayer1,
                gradientLayer2
            }
        },
        methods: {
            // 全局显示错误消息的方法
            showNotification(message, type) {
                this.$refs.notificationManager.addMessage(message, type);
            }
        },
        mounted() {
            // 设置错误管理器
            setNotificationManager(this.$refs.notificationManager);

            // 设置全局错误处理
            setupGlobalNotificationHandling();
        }
    }
</script>

<style>
    #app {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        color: #2c3e50;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        background: linear-gradient(135deg, #f5f7fa 0%, #e4e7f1 100%);
        position: relative;
        overflow-x: hidden;
    }

    .dynamic-background {
        position: fixed;
        inset: 0;
        z-index: 0;
        pointer-events: none;
    }

    /* 渐变背景层 */
    .gradient-background {
        position: absolute;
        inset: 0;
        background-size: 600% 600%; /* 让移动更明显 */
        animation: gradientShift 20s linear infinite;
        opacity: 0;
        transition: opacity 2s ease-in-out; /* 添加过渡效果 */
    }

        /* 激活的层 */
        .gradient-background.active {
            opacity: 1;
        }

        /* 淡出效果的层 */
        .gradient-background.fade-out {
            opacity: 0;
        }

    /* 层1在层2之上 */
    .gradient-layer1 {
        z-index: 1;
    }

    .gradient-layer2 {
        z-index: 0;
    }

    @keyframes gradientShift {
        0% {
            background-position: 0% 50%;
        }

        50% {
            background-position: 100% 50%;
        }

        100% {
            background-position: 0% 50%;
        }
    }

    /* 透明画布 */
    .dynamic-background canvas {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        display: block;
        z-index: 2; /* 确保在背景之上 */
    }

    .foreground-container {
        position: relative;
        z-index: 10; /* 高于背景层 */
        display: flex;
        flex-direction: column;
        min-height: 100vh;
    }

    .content {
        flex: 1;
        padding: 20px;
        margin: 0 auto;
        width: 90%;
        position: relative;
        z-index: 3;
    }

    .app-footer {
        background-color: #2c3e50;
        color: white;
        text-align: center;
        padding: 15px 0;
        margin-top: 30px;
        position: relative;
        /*        z-index: 2;*/
    }

    /* 全局按钮样式 */
    .primary-btn {
        background-color: #3498db;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 600;
        transition: background-color 0.3s;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    }

        .primary-btn:hover {
            background-color: #2980b9;
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

    .danger-btn {
        background-color: #e74c3c;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 600;
        transition: background-color 0.3s;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    }

        .danger-btn:hover {
            background-color: #c0392b;
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

    .success-btn {
        background-color: #2ecc71;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 600;
        transition: background-color 0.3s;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    }

        .success-btn:hover {
            background-color: #27ae60;
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

    /* 全局卡片样式 */
    .card {
        background: rgba(255, 255, 255, 0.9);
        border-radius: 12px;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
        padding: 25px;
        margin-bottom: 25px;
        transition: transform 0.3s, box-shadow 0.3s;
        backdrop-filter: blur(4px);
        border: 1px solid rgba(255, 255, 255, 0.5);
    }

        .card:hover {
            transform: translateY(-8px);
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
            background: rgba(255, 255, 255, 0.95);
        }

    /* 页面淡入淡出效果 */
    .page-fade-enter-active,
    .page-fade-leave-active {
        transition: all 0.3s ease;
    }

    .page-fade-enter-from,
    .page-fade-leave-to {
        opacity: 0;
        transform: translateY(20px);
    }

    /* 确保过渡期间容器保持高度 */
    .content {
        position: relative;
        min-height: 70vh; /* 添加最小高度防止页面跳动 */
    }

    /* 响应式设计 */
    @media (max-width: 768px) {
        .content {
            padding: 0px;
            width: 100%;
        }

        .card {
            padding: 15px;
        }
    }
</style>